import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Spinner } from '../components/ui'
import { AppLayout } from '../components/layout/AppLayout'
import { SectionErrorBoundary } from '../components/ErrorBoundary'
import { RequireAdmin, RequireAuth } from './RequireAuth'

const DashboardPage = lazy(() =>
  import('../features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const ProjectsPage = lazy(() => import('../features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })))
const IdeasPage = lazy(() => import('../features/ideas/IdeasPage').then((m) => ({ default: m.IdeasPage })))
const CalendarPage = lazy(() => import('../features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })))
const ModulesPlaceholderPage = lazy(() =>
  import('../features/modules/ModulesPlaceholderPage').then((m) => ({ default: m.ModulesPlaceholderPage })),
)
const AdminPage = lazy(() => import('../features/admin/AdminPage').then((m) => ({ default: m.AdminPage })))
const ResetPasswordPage = lazy(() =>
  import('../features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const FinanceDashboardPage = lazy(() =>
  import('../features/finance/FinanceDashboardPage').then((m) => ({ default: m.FinanceDashboardPage })),
)
const MovementsPage = lazy(() => import('../features/finance/MovementsPage').then((m) => ({ default: m.MovementsPage })))
const CardsPage = lazy(() => import('../features/finance/CardsPage').then((m) => ({ default: m.CardsPage })))
const AccountsPage = lazy(() => import('../features/finance/AccountsPage').then((m) => ({ default: m.AccountsPage })))
const InvestmentsPage = lazy(() =>
  import('../features/finance/InvestmentsPage').then((m) => ({ default: m.InvestmentsPage })),
)
const BudgetsPage = lazy(() => import('../features/finance/BudgetsPage').then((m) => ({ default: m.BudgetsPage })))
const AccountStatementPage = lazy(() =>
  import('../features/finance/AccountStatementPage').then((m) => ({ default: m.AccountStatementPage })),
)
const CardStatementPage = lazy(() =>
  import('../features/finance/CardStatementPage').then((m) => ({ default: m.CardStatementPage })),
)

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <SectionErrorBoundary>
      <Suspense fallback={<Spinner />}>{children}</Suspense>
    </SectionErrorBoundary>
  )
}

function ProtectedApp() {
  return (
    <RequireAuth>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <LazyPage>
                <DashboardPage />
              </LazyPage>
            }
          />
          <Route
            path="/projects"
            element={
              <LazyPage>
                <ProjectsPage />
              </LazyPage>
            }
          />
          <Route path="/tasks" element={<Navigate to="/projects" replace />} />
          <Route
            path="/ideas"
            element={
              <LazyPage>
                <IdeasPage />
              </LazyPage>
            }
          />
          <Route
            path="/calendar"
            element={
              <LazyPage>
                <CalendarPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance"
            element={
              <LazyPage>
                <FinanceDashboardPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/movements"
            element={
              <LazyPage>
                <MovementsPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/cards"
            element={
              <LazyPage>
                <CardsPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/accounts"
            element={
              <LazyPage>
                <AccountsPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/investments"
            element={
              <LazyPage>
                <InvestmentsPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/budget"
            element={
              <LazyPage>
                <BudgetsPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/accounts/:accountId/movements"
            element={
              <LazyPage>
                <AccountStatementPage />
              </LazyPage>
            }
          />
          <Route
            path="/finance/cards/:cardId/movements"
            element={
              <LazyPage>
                <CardStatementPage />
              </LazyPage>
            }
          />
          <Route
            path="/modules/:name"
            element={
              <LazyPage>
                <ModulesPlaceholderPage />
              </LazyPage>
            }
          />
          <Route
            path="/admin"
            element={
              <LazyPage>
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              </LazyPage>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </RequireAuth>
  )
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/reset"
        element={
          <LazyPage>
            <ResetPasswordPage />
          </LazyPage>
        }
      />
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  )
}
